import React from 'react';
import { ShoppingBag, RefreshCcw, CheckCircle, Clock, XCircle, Package } from 'lucide-react';
import { OrderHistory } from '../services/orderLinkAPI';

interface OrderHistoryPageProps {
  linkId: string;
  orders: OrderHistory[];
  onReorder: (order: OrderHistory) => void;
  isLoading: boolean;
}

const OrderHistoryPage: React.FC<OrderHistoryPageProps> = ({
  linkId,
  orders,
  onReorder,
  isLoading
}) => {
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      'finalizado': {
        label: 'Finalizado',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className="w-3 h-3" />
      },
      'cancelado': {
        label: 'Cancelado',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className="w-3 h-3" />
      },
      'em preparo': {
        label: 'Em Preparo',
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <Clock className="w-3 h-3" />
      },
      'caminho': {
        label: 'A Caminho',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <Package className="w-3 h-3" />
      },
      'aceito': {
        label: 'Aceito',
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: <CheckCircle className="w-3 h-3" />
      },
      'confirmado': {
        label: 'Confirmado',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <CheckCircle className="w-3 h-3" />
      }
    };

    const config = statusConfig[status] || statusConfig['aceito'];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return `Hoje às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInHours < 48) {
      return `Ontem às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getItemsSummary = (order: OrderHistory): string => {
    const firstItems = order.items.slice(0, 2);
    const summary = firstItems.map(item => `${item.quantity}x ${item.product_name}`).join(', ');
    const remaining = order.total_items - firstItems.reduce((sum, item) => sum + item.quantity, 0);

    if (remaining > 0) {
      return `${summary} +${remaining} ${remaining === 1 ? 'item' : 'itens'}`;
    }

    return summary;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-gray-200 border-t-transparent rounded-full mx-auto mb-4" style={{ borderTopColor: 'var(--bg-color)' }}></div>
          <p className="text-gray-600">Carregando pedidos...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900 mb-2">
            Nenhum pedido ainda
          </h2>
          <p className="text-gray-500 text-[14px]">
            Você ainda não fez nenhum pedido. Que tal fazer seu primeiro pedido agora?
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="text-[22px] font-bold text-gray-900 mb-6">
          Meus Pedidos
        </h1>

        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Order Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold text-gray-700">
                      Pedido #{order.daily_order_id}
                    </span>
                    {getStatusBadge(order.status)}
                  </div>
                  <span className="text-[12px] text-gray-500">
                    {formatDate(order.created_at)}
                  </span>
                </div>

                {/* Items Summary */}
                <p className="text-[13px] text-gray-600 mb-2 line-clamp-1">
                  {getItemsSummary(order)}
                </p>

                {/* Payment Method */}
                <div className="flex items-center gap-4 text-[12px] text-gray-500">
                  <span>{order.meio_pagamento}</span>
                  {order.retirada_local && (
                    <span>Retirada no local</span>
                  )}
                </div>
              </div>

              {/* Order Footer */}
              <div className="p-4 bg-gray-50 flex items-center justify-between">
                <div>
                  <span className="text-[12px] text-gray-500">Total</span>
                  <p className="text-[18px] font-bold" style={{ color: 'var(--text-color)' }}>
                    R$ {order.order_price?.toFixed(2).replace('.', ',') || '0,00'}
                  </p>
                </div>

                <button
                  onClick={() => onReorder(order)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-semibold text-white rounded-lg transition-colors hover:opacity-90"
                  style={{ backgroundColor: 'var(--bg-color)' }}
                >
                  <RefreshCcw className="w-4 h-4" />
                  Fazer Pedido Novamente
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryPage;
